/**
 * 🌍 Location Context
 * 사용자 위치 및 동네 설정 관리
 */

import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import {
  UserLocation,
  getUserLocation,
  saveUserLocation,
  clearUserLocation,
  getCurrentPosition,
} from "@/lib/geolocation";

interface LocationContextType {
  userLocation: UserLocation | null;
  setUserLocation: (location: UserLocation | null) => void;
  requestLocation: () => Promise<void>;
  clearLocation: () => void;
  isLoadingLocation: boolean;
  locationError: string | null;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

export function LocationProvider({ children }: { children: ReactNode }) {
  const [userLocation, setUserLocationState] = useState<UserLocation | null>(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);

  // Load saved location on mount
  useEffect(() => {
    const saved = getUserLocation();
    if (saved) {
      setUserLocationState(saved);
    }
  }, []);

  const setUserLocation = (location: UserLocation | null) => {
    if (location) {
      saveUserLocation(location);
      setUserLocationState(location);
    } else {
      clearUserLocation();
      setUserLocationState(null);
    }
    setLocationError(null);
  };

  const requestLocation = async () => {
    setIsLoadingLocation(true);
    setLocationError(null);
    
    try {
      const position = await getCurrentPosition();
      const newLocation: UserLocation = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        timestamp: Date.now(),
      };
      
      setUserLocation(newLocation);
    } catch (error) {
      const message = error instanceof Error ? error.message : "위치 정보를 가져올 수 없습니다";
      setLocationError(message);
      console.error("Failed to get location:", error);
    } finally {
      setIsLoadingLocation(false);
    }
  };

  const clearLocation = () => {
    clearUserLocation();
    setUserLocationState(null);
    setLocationError(null);
  };

  return (
    <LocationContext.Provider
      value={{
        userLocation,
        setUserLocation,
        requestLocation,
        clearLocation,
        isLoadingLocation,
        locationError,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
}

export function useLocation() {
  const context = useContext(LocationContext);
  if (context === undefined) {
    throw new Error("useLocation must be used within a LocationProvider");
  }
  return context;
}
